/**
 * Mini Games Component
 * VOY-133: Quick free games for practice and coin earning
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Zap, Clock, Coins, Brain } from 'lucide-react';

interface MiniGame {
  id: string;
  name: string;
  icon: string;
  description: string;
  duration: string;
  reward: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

interface MiniGamesProps {
  games: MiniGame[];
  onPlayGame: (gameId: string) => void;
}

export function MiniGames({ games, onPlayGame }: MiniGamesProps) {
  return (
    <Card className="rounded-3xl bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl border border-white/30 dark:border-zinc-700/50 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-5 h-5 text-yellow-500" />
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Quick Play (FREE)</h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-zinc-400 mb-4">
          Warm up before the real game - No entry fee!
        </p>

        {/* Mini Game Cards */}
        <div className="space-y-3">
          {games.map((game) => (
            <div
              key={game.id}
              className="bg-white/[0.08] dark:bg-zinc-700/50 backdrop-blur-xl rounded-3xl p-4 border border-white/30 dark:border-zinc-600/50 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{game.icon}</span>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">{game.name}</h4>
                    <p className="text-xs text-gray-600 dark:text-zinc-400">{game.description}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex gap-3 text-xs text-gray-600 dark:text-zinc-400">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {game.duration}
                  </span>
                  <span className="flex items-center gap-1">
                    <Brain className="w-3 h-3" />
                    {game.difficulty}
                  </span>
                  <span className="flex items-center gap-1 text-green-600 dark:text-green-400 font-semibold">
                    <Coins className="w-3 h-3" />
                    {game.reward}
                  </span>
                </div>
                <Button
                  onClick={() => onPlayGame(game.id)}
                  size="sm"
                  className="bg-[#5B6FED] hover:bg-[#4A5FDD] rounded-full"
                >
                  Play Free
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Benefits */}
        <div className="mt-4 bg-white/[0.08] dark:bg-zinc-700/50 backdrop-blur-xl border border-white/30 dark:border-zinc-600/50 rounded-3xl p-3 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl">
          <p className="text-xs text-green-800 dark:text-green-400">
            <strong>💡 Benefits:</strong> Earn coins, practice boosters, improve skills
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

